import { cn } from '@/lib/tailwind-utils';
import type { CoachPortalSession } from '@airreading/common/data';
import { useFeatureFlag } from '@airreading/feature-flags/web';
import { ClipboardCheck } from 'lucide-react';
import { useInAppAssessment } from './in-app-assessment-provider';
import { canRunInAppAssessments } from './in-app-assessment-utils';

/** Same gate as {@link InAppAssessmentTrigger}: flag on, and the class has assessments to run. */
// eslint-disable-next-line react-refresh/only-export-components
export function useInAppAssessmentTriggerVisible(session: CoachPortalSession | undefined): boolean {
  const enabled = useFeatureFlag('in-app-pm-gla', false);
  return Boolean(session && enabled && canRunInAppAssessments(session));
}

/**
 * Opens the in-app assessment window for a class.
 *
 * Renders nothing unless the `in-app-pm-gla` flag is on and the class actually has in-app assessments
 * to run, which also keeps it hidden on the past-classes table since that view does not load them.
 */
export function InAppAssessmentTrigger({
  session,
  className,
  dataTestId,
}: {
  session: CoachPortalSession;
  className?: string;
  dataTestId?: string;
}) {
  const visible = useInAppAssessmentTriggerVisible(session);
  const { open } = useInAppAssessment();

  if (!visible) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => {
        void open(session);
      }}
      className={cn(
        'inline-flex items-center gap-1 rounded-sm text-air-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className,
      )}
      data-testid={dataTestId}
    >
      <ClipboardCheck className="h-4 w-4 shrink-0" aria-hidden />
      Open assessment
    </button>
  );
}
